/**
 * React Query hooks for Home Tests (Spirometry & 6MWT)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  testsApi,
  type SpirometryCreate,
  type SixMWTCreate,
  type SpirometryResult,
  type SixMWTResult,
} from '../api/tests';

// === SPIROMETRY HOOKS ===

/**
 * Hook to get spirometry test history
 */
export function useSpirometryHistory(
  patientId: number,
  params?: { limit?: number; days?: number }
) {
  return useQuery({
    queryKey: ['spirometry', patientId, params],
    queryFn: () => testsApi.getSpirometryHistory(patientId, params),
    enabled: !!patientId,
  });
}

/**
 * ✅ NEW: Hook to get spirometry test history for current patient (PATIENT role)
 */
export function useMySpirometryHistory(
  params?: { limit?: number; days?: number },
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['spirometry', 'me', params],
    queryFn: () => testsApi.getMySpirometryHistory(params),
    enabled,
  });
}

/**
 * Hook to record a new spirometry test
 */
export function useRecordSpirometry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SpirometryCreate) => testsApi.recordSpirometry(data),
    onSuccess: (response, variables) => {
      toast.success('Spirometry test recorded!', {
        description: `FEV1: ${response.fev1_percent_predicted}% of predicted`,
      });
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['spirometry', variables.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['allTests', variables.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['trends', variables.patient_id, 'fev1'] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard', variables.patient_id] });
    },
    onError: (error: any) => {
      toast.error('Failed to record spirometry test', {
        description: error.message || 'Please try again',
      });
    },
  });
}

// === 6MWT HOOKS ===

/**
 * Hook to get 6MWT history
 */
export function useSixMWTHistory(
  patientId: number,
  params?: { limit?: number; days?: number }
) {
  return useQuery({
    queryKey: ['sixMWT', patientId, params],
    queryFn: () => testsApi.getSixMWTHistory(patientId, params),
    enabled: !!patientId,
  });
}

/**
 * ✅ NEW: Hook to get 6MWT history for current patient (PATIENT role)
 */
export function useMySixMWTHistory(
  params?: { limit?: number; days?: number },
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['sixMWT', 'me', params],
    queryFn: () => testsApi.getMySixMWTHistory(params),
    enabled,
  });
}

/**
 * Hook to record a new 6MWT
 */
export function useRecordSixMWT() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SixMWTCreate) => testsApi.recordSixMWT(data),
    onSuccess: (response, variables) => {
      toast.success('6-Minute Walk Test recorded!', {
        description: `Distance: ${response.distance_meters}m`,
      });
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['sixMWT', variables.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['allTests', variables.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['trends', variables.patient_id, 'six-mwt'] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard', variables.patient_id] });
    },
    onError: (error: any) => {
      toast.error('Failed to record 6MWT', {
        description: error.message || 'Please try again',
      });
    },
  });
}

// Alias for backwards compatibility
export const useRecord6MWT = useRecordSixMWT;

// === COMBINED TEST HISTORY ===

/**
 * Hook to get all test history (both Spirometry and 6MWT)
 */
export function useAllTests(
  patientId: number,
  params?: { limit?: number; days?: number }
) {
  return useQuery({
    queryKey: ['allTests', patientId, params],
    queryFn: () => testsApi.getAllTests(patientId, params),
    enabled: !!patientId,
  });
}

// Alias for backwards compatibility
export const usePatientTests = useAllTests;

// === UTILITY HOOKS ===

/**
 * Hook to get latest test results
 */
export function useLatestTests(patientId: number) {
  const { data: spirometryData } = useSpirometryHistory(patientId, { limit: 1 });
  const { data: sixMWTData } = useSixMWTHistory(patientId, { limit: 1 });

  return {
    latestSpirometry: spirometryData?.results?.[0] || null,
    latestSixMWT: sixMWTData?.results?.[0] || null,
    isLoading: !spirometryData || !sixMWTData,
  };
}

/**
 * Hook to get test statistics
 */
export function useTestStats(patientId: number, days: number = 30) {
  const { data: allTests, isLoading } = useAllTests(patientId, { days });

  if (isLoading || !allTests) {
    return {
      totalTests: 0,
      spirometryCount: 0,
      sixMWTCount: 0,
      isLoading: true,
    };
  }

  return {
    totalTests: allTests.total_tests,
    spirometryCount: allTests.spirometry.count,
    sixMWTCount: allTests.six_mwt.count,
    isLoading: false,
  };
}

// === UPDATE & DELETE HOOKS ===

/**
 * Hook to update spirometry test
 */
export function useUpdateSpirometry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ testId, data }: { testId: number; data: Partial<SpirometryCreate> }) =>
      testsApi.updateSpirometry(testId, data),
    onSuccess: (response, variables) => {
      toast.success('Spirometry test updated!', {
        description: 'Your test results have been updated successfully',
      });
      
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['spirometry'] });
      queryClient.invalidateQueries({ queryKey: ['allTests'] });
      queryClient.invalidateQueries({ queryKey: ['trends'] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard'] });
    },
    onError: (error: any) => {
      toast.error('Failed to update spirometry test', {
        description: error.message || 'Please try again',
      });
    },
  });
}

/**
 * Hook to delete spirometry test
 */
export function useDeleteSpirometry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (testId: number) => testsApi.deleteSpirometry(testId),
    onSuccess: () => {
      toast.success('Spirometry test deleted', {
        description: 'The test has been removed from your records',
      });
      
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['spirometry'] });
      queryClient.invalidateQueries({ queryKey: ['allTests'] });
      queryClient.invalidateQueries({ queryKey: ['trends'] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard'] });
    },
    onError: (error: any) => {
      toast.error('Failed to delete spirometry test', {
        description: error.message || 'Please try again',
      });
    },
  });
}

/**
 * Hook to update 6MWT test
 */
export function useUpdateSixMWT() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ testId, data }: { testId: number; data: Partial<SixMWTCreate> }) =>
      testsApi.updateSixMWT(testId, data),
    onSuccess: (response, variables) => {
      toast.success('6-Minute Walk Test updated!', {
        description: 'Your test results have been updated successfully',
      });
      
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['sixMWT'] });
      queryClient.invalidateQueries({ queryKey: ['allTests'] });
      queryClient.invalidateQueries({ queryKey: ['trends'] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard'] });
    },
    onError: (error: any) => {
      toast.error('Failed to update 6MWT test', {
        description: error.message || 'Please try again',
      });
    },
  });
}

/**
 * Hook to delete 6MWT test
 */
export function useDeleteSixMWT() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (testId: number) => testsApi.deleteSixMWT(testId),
    onSuccess: () => {
      toast.success('6-Minute Walk Test deleted', {
        description: 'The test has been removed from your records',
      });
      
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['sixMWT'] });
      queryClient.invalidateQueries({ queryKey: ['allTests'] });
      queryClient.invalidateQueries({ queryKey: ['trends'] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard'] });
    },
    onError: (error: any) => {
      toast.error('Failed to delete 6MWT test', {
        description: error.message || 'Please try again',
      });
    },
  });
}

// === HOME TEST ADHERENCE ===

export interface HomeTestAdherence {
  adherence_rate: number;
  completed: number;
  scheduled: number;
  pending: number;
  missed: number;
  spirometry_completed: number;
  six_mwt_completed: number;
  spirometry_scheduled: number;
  six_mwt_scheduled: number;
  risk_level: string;
}

/**
 * Hook to get home test adherence for current patient
 * Calculates adherence based on prescribed schedules and completed tests
 */
export function useHomeTestAdherence(days: number = 30) {
  return useQuery<HomeTestAdherence>({
    queryKey: ['homeTestAdherence', days],
    queryFn: () => testsApi.getMyHomeTestAdherence(days),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}